import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { FileText, HelpCircle, Loader2 } from "lucide-react";
import { MultiSelect } from "../../../components/ui/multiselect";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "@/utils/api";
import {
  setData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/oldConventionalSlice";
import FilePreview from "../../../components/common/FilePreview";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const modernizationSchema = (finalMaxLooms) =>
  z.object({
    no_of_looms_to_upgrade: z
      .string()
      .min(1, "Number of looms is required")
      .refine(
        (val) => {
          const num = Number(val);
          return num >= 1 && num <= finalMaxLooms;
        },
        {
          message:
            "You have entered more than your existing looms. Please enter a valid number.",
        }
      ),
    loom_purchases: z
      .array(
        z.object({
          loom_name: z.string(),
          serial_number: z.string().optional(),
          purchase_date: z
            .string()
            .min(1, "Purchase date is required")
            .refine((date) => new Date(date) <= new Date("2022-09-01"), {
              message: "Date of purchase must be before 01.09.2022",
            }),
          invoice_copy: z.any().optional(),
          attachments: z
            .array(z.string())
            .min(1, "Select at least one attachment kit"),
        })
      )
      .min(1, "Add at least one loom entry"),
  });

const ATTACHMENT_OPTIONS = [
  { id: "Complete Rapier Kit", name: "Complete Rapier Kit" },
  { id: "Electronic Color Selector", name: "Electronic Color Selector" },
  { id: "Weft Accumulator", name: "Weft Accumulator" },
  { id: "Weft Sensor", name: "Weft Sensor" },
  { id: "Gear Box", name: "Gear Box" },
  { id: "Panel Board", name: "Panel Board" },
  { id: "Tuck in Device", name: "Tuck in Device" },
];

const ModernizationDetails = ({ data = {} }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { available_limit } = useSelector((state) => state.oldConventional);
  const rawMaxLooms = data?.beneficiary_loom_details?.ordinary_powerlooms ?? 10;
  const maxLooms = Math.min(rawMaxLooms, 10);
  const finalMaxLooms = Math.min(maxLooms, available_limit.limit);
  const [buttonType, setButtonType] = useState("submit");
  const location = useLocation();
  const nav = useNavigate();
  const dispatch = useDispatch(); 

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(modernizationSchema(finalMaxLooms)),
    defaultValues: {
      no_of_looms_to_upgrade:
        data?.loom_upgradations?.no_of_looms_to_upgrade ?? "",
      loom_purchases: data?.loom_upgradations?.loom_purchases ?? [],
    },
  });

  const formData = watch();
  const loomCount = watch("no_of_looms_to_upgrade");

  useEffect(() => {
    const count = Number(loomCount);
    if (!count || count < 1) return;

    setValue(
      "loom_purchases",
      Array.from({ length: count }).map((_, i) => {
        const backendLoom = data?.loom_upgradations?.loom_purchases?.[i];

        return {
          loom_name: backendLoom?.loom_name ?? `Loom ${i + 1}`,
          serial_number: backendLoom?.serial_number ?? "",
          purchase_date: backendLoom?.purchase_date ?? "",
          invoice_copy: backendLoom?.invoice_copy ?? "",
          attachments: backendLoom
            ? backendLoom.attachments.map((att) => att.attachment_name)
            : [],
        };
      })
    );
  }, [loomCount, data, setValue]);

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "loom_purchases") {
          submitData.append(
            key,
            JSON.stringify(
              value.map((loom, i) => ({
                loom_name: loom.loom_name,
                serial_number: loom.serial_number,
                purchase_date: loom.purchase_date,
                attachments: loom.attachments,
                invoice_copy:
                  loom.invoice_copy instanceof File
                    ? `invoice_copy_${i}`
                    : loom.invoice_copy || "",
              }))
            )
          );

          value.forEach((loom, i) => {
            if (loom.invoice_copy instanceof File) {
              submitData.append(`invoice_copy_${i}`, loom.invoice_copy);
            } else {
              submitData.append(`invoice_copy_${i}`, loom.invoice_copy);
            }
          });
        } else {
          if (Array.isArray(value)) {
            submitData.append(key, JSON.stringify(value));
          } else if (value instanceof File) {
            submitData.append(key, value);
          } else if (value !== undefined && value !== null) {
            submitData.append(key, value);
          }
        }
      });

      if (data?.id) submitData.append("id", data.id);

      const response = await api.post(
        "/modernization/upgradation-loom",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      dispatch(setData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application saved successfully.",
        variant: "success",
      });

      searchParams?.delete("tab");
      // nav(
      //   {
      //     pathname: location.pathname,
      //     search: searchParams.toString(),
      //   },
      //   { replace: true }
      // );

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(setCurrentTab("bank"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Upgradation Details
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 mt-4">
          {/* Number of looms */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="mt-5 space-y-2">
              <Label required>
                Number of conventional looms proposed to be upgraded
              </Label>
              <Input
                type="text"
                value={formData.no_of_looms_to_upgrade || ""}
                onInput={(e) => {
                  let value = e.target.value.replace(/\D/g, "");

                  if (value === "" || value === "0") {
                    setValue("no_of_looms_to_upgrade", "");
                    return;
                  }

                  const num = Number(value);
                  if (num > finalMaxLooms) {
                    setValue("no_of_looms_to_upgrade", String(finalMaxLooms));
                  } else {
                    setValue("no_of_looms_to_upgrade", value);
                  }
                }}
                {...register("no_of_looms_to_upgrade")}
              />
              {errors.no_of_looms_to_upgrade && (
                <p className="text-sm text-red-500">
                  {errors.no_of_looms_to_upgrade.message}
                </p>
              )}
              <p className="ms-1 text-[12px] text-muted-foreground" style={{ fontSize: '18px' }}>
                Maximum <b>{finalMaxLooms} Loom</b> Allowed
              </p>
            </div>
          </div>

          {/* Loom Table */}
          <div className="my-7 rounded-lg overflow-hidden">
            <h3 className="text-xl font-bold">
              Date of Purchase of 3 year old conventional Looms to be upgraded
              as Rapier Shuttleless Loom
              <span className="text-red-500">*</span>
            </h3>

            {loomCount > 0 ? (
              <table className="w-full mt-5 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">S.No.</th>
                    <th className="px-4 py-3 text-left">Loom Name</th>
                    <th className="px-4 py-3 text-left">Serial Number</th>
                    <th className="px-4 py-3 text-left flex items-center gap-2">
                      Purchase Date{" "}
                      <Popover>
                        <PopoverTrigger>
                          <HelpCircle className="text-primary" />
                        </PopoverTrigger>
                        <PopoverContent>
                          The Purchase date must be 3 years before the G.O. Date
                          02.09.2025
                        </PopoverContent>
                      </Popover>
                    </th>
                    <th className="px-4 py-3 text-left">Invoice Copy</th>
                    <th className="px-4 py-3 text-left">Attachments / Kits</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {formData.loom_purchases.map((_, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3">
                        <Input
                          {...register(`loom_purchases.${index}.loom_name`)}
                          readOnly
                          className="bg-gray-100 cursor-not-allowed"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          placeholder="Enter serial number"
                          {...register(`loom_purchases.${index}.serial_number`)}
                        />
                        {errors?.loom_purchases?.[index]?.serial_number && (
                          <p className="text-xs text-red-500">
                            {errors.loom_purchases[index].serial_number.message}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="date"
                          max="2022-09-01"
                          {...register(`loom_purchases.${index}.purchase_date`)}
                        />
                        {errors?.loom_purchases?.[index]?.purchase_date && (
                          <p className="text-xs text-red-500">
                            {errors.loom_purchases[index].purchase_date.message}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Controller
                          control={control}
                          name={`loom_purchases.${index}.invoice_copy`}
                          render={({
                            field: { value, onChange, ...field },
                          }) => (
                            <>
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  onChange(file);
                                }}
                                {...field}
                              />
                              <FilePreview
                                fieldName={`loom_purchases.${index}.invoice_copy`}
                                preview={
                                  formData.loom_purchases[index]?.preview
                                }
                                file={value instanceof File ? value : null}
                                existingFile={
                                  typeof value === "string" ? value : null
                                }
                                onRemove={() => onChange(null)}
                              />
                            </>
                          )}
                        />
                        {errors?.loom_purchases?.[index]?.invoice_copy && (
                          <p className="text-xs text-red-500">
                            {errors.loom_purchases[index].invoice_copy.message}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Controller
                          control={control}
                          name={`loom_purchases.${index}.attachments`}
                          render={({ field }) => (
                            <MultiSelect
                              options={ATTACHMENT_OPTIONS}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select"
                            />
                          )}
                        />
                        {errors?.loom_purchases?.[index]?.attachments && (
                          <p className="text-xs text-red-500">
                            {errors.loom_purchases[index].attachments.message}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-5">
                <h5 className="text-primary">
                  !!! Please Enter the Number of conventional looms proposed to
                  be upgraded. !!!
                </h5>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6 gap-3">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[160px]"
              onClick={() => setButtonType("submit")}
            >
              {isSubmitting && buttonType === "submit" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[160px]"
            >
              {isSubmitting && buttonType === "next" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default ModernizationDetails;
